/**
 * Constants for the Action Center (golden_gate_in_app_notification_page_v2).
 *
 * String constants are Panther identifiers, not display text. Resolve them through
 * `t(bundle, id)` from '@src/translationHelper' — the English source lives in
 * resources/translations.puff.json.
 */

import { ActionCenterPriority, KnownCategory } from './types'

// Panther string ids
export const ACTION_CENTER_TITLE = 'jsma-web-actionCenter-title'
export const ALL_STORES_TAB = 'jsma-web-actionCenter-allStoresTab'
export const CURRENT_STORE_TAB = 'jsma-web-actionCenter-currentStoreTab'
export const VIEW_ONLY_TITLE = 'jsma-web-actionCenter-viewOnlyTitle'
export const VIEW_ONLY_MESSAGE = 'jsma-web-actionCenter-viewOnlyMessage'
export const NEEDS_IMMEDIATE_ATTENTION = 'jsma-web-actionCenter-needsImmediateAttention'
export const TASK_COUNT = 'jsma-web-actionCenter-taskCount'
export const CATEGORY_OPERATIONAL = 'jsma-web-actionCenter-categoryOperational'
export const CATEGORY_PLANOGRAM = 'jsma-web-actionCenter-categoryPlanogram'
export const CATEGORY_THEFT = 'jsma-web-actionCenter-categoryTheft'
export const CATEGORY_OTHER = 'jsma-web-actionCenter-categoryOther'
export const BACK = 'jsma-web-actionCenter-back'
export const ACKNOWLEDGE = 'jsma-web-actionCenter-acknowledge'
export const ACKNOWLEDGE_ERROR = 'jsma-web-actionCenter-acknowledgeError'
export const LOADING = 'jsma-web-actionCenter-loading'
export const LOAD_ERROR = 'jsma-web-actionCenter-loadError'
export const STORE_LOAD_ERROR = 'jsma-web-actionCenter-storeLoadError'
export const EMPTY_ALL_CAUGHT_UP = 'jsma-web-actionCenter-emptyAllCaughtUp'
export const OPEN_DETAILS = 'jsma-web-actionCenter-openDetails'
export const TRUNCATED_NOTICE = 'jsma-web-actionCenter-truncatedNotice'
export const PRIORITY_CRITICAL = 'jsma-web-actionCenter-priorityCritical'
export const PRIORITY_HIGH = 'jsma-web-actionCenter-priorityHigh'
export const PRIORITY_MEDIUM = 'jsma-web-actionCenter-priorityMedium'
export const PRIORITY_LOW = 'jsma-web-actionCenter-priorityLow'
export const UNREAD = 'jsma-web-actionCenter-unread'

/**
 * Store filter (store-scope picker opened from the "All stores" tab). These reuse the
 * already-translated legacy notifications-filter strings, so the filter ships with no new Panther
 * ids. The ids are stable Panther keys; duplicating the key here keeps the Action Center
 * self-contained and decoupled from the legacy NotificationsPage lifecycle.
 */
export const FILTER_HEADING = 'jsma-web-notifications-filterHeading'
export const APPLY_FILTERS = 'jsma-web-notifications-applyFilters'
export const CLEAR_FILTER = 'jsma-web-notifications-clearFilter'
export const STORE_SELECTION_SUMMARY = 'jsma-web-notifications-storeSelectionSummary'
export const CLOSE = 'jsma-web-common-close'
// Same store-search placeholder as the store selection page and legacy notifications filter.
export { SEARCH_PLACEHOLDER as SEARCH_STORES_PLACEHOLDER } from '@pages/StoreSelectionPage/constants'
export { NO_STORES_FOUND } from '@pages/StoreSelectionPage/constants'

/**
 * Literal storeId the backend stamps on store-agnostic notifications. It is not a real store, so it
 * is kept out of the store-filter options -- GLOBAL items still show unfiltered, but you cannot
 * "scope to GLOBAL" (P519064327).
 */
export const STORE_AGNOSTIC_STORE_ID = 'GLOBAL'

/** Priorities that surface in the "Needs immediate attention" section, in display order. */
export const IMMEDIATE_ATTENTION_PRIORITIES: ReadonlyArray<ActionCenterPriority> = ['CRITICAL', 'HIGH']

/** Sort rank: lower renders first. Unknown priorities sort last. */
export const PRIORITY_RANK: Readonly<Record<ActionCenterPriority, number>> = {
    CRITICAL: 0,
    HIGH: 1,
    MEDIUM: 2,
    LOW: 3,
}

/**
 * Severity colour token per priority. Rendered as a CSS modifier class on the card icon
 * (`action-center-icon--<tone>`), see ActionCenterPage.scss.
 */
export type SeverityTone = 'red' | 'orange' | 'yellow'
export const PRIORITY_TONE: Readonly<Record<ActionCenterPriority, SeverityTone>> = {
    CRITICAL: 'red',
    HIGH: 'red',
    MEDIUM: 'orange',
    LOW: 'yellow',
}

/**
 * Spoken counterpart of PRIORITY_TONE: the Panther id read to screen-reader users, since the
 * severity is otherwise conveyed by colour alone.
 */
export const PRIORITY_LABEL: Readonly<Record<ActionCenterPriority, string>> = {
    CRITICAL: PRIORITY_CRITICAL,
    HIGH: PRIORITY_HIGH,
    MEDIUM: PRIORITY_MEDIUM,
    LOW: PRIORITY_LOW,
}

/** Categories with a dedicated icon and label, in accordion display order. */
export const KNOWN_CATEGORIES: ReadonlyArray<KnownCategory> = ['OPERATIONAL', 'PLANOGRAM', 'THEFT']

export const CATEGORY_LABEL: Readonly<Record<KnownCategory, string>> = {
    OPERATIONAL: CATEGORY_OPERATIONAL,
    PLANOGRAM: CATEGORY_PLANOGRAM,
    THEFT: CATEGORY_THEFT,
}

/** Page size for the inbox drain. The service caps a page at 50. */
export const LIST_PAGE_SIZE = 50

/**
 * Upper bound on pages fetched per read. The service pages newest-first with no priority
 * ordering and, on the unread scope, no time window, so an inbox past this many pages is
 * truncated to its newest items. The read then tops up CRITICAL and HIGH through their own
 * priority-filtered reads (see ActionCenterAPIService), so the cap only ever drops MEDIUM and
 * LOW items, and the UI says so (TRUNCATED_NOTICE).
 */
export const LIST_MAX_PAGES = 10

/** Most items one read returns before the top-up kicks in. Quoted in TRUNCATED_NOTICE. */
export const LIST_MAX_ITEMS = LIST_PAGE_SIZE * LIST_MAX_PAGES

/**
 * The bell badge is fed by a single page (LIST_PAGE_SIZE items). When that page did not
 * exhaust the inbox the badge shows "50+", via Meridian Badge's `maxValue`, rather than paying
 * for a full drain just to print a number; opening the sheet publishes the exact total.
 */
export const BADGE_MAX_VALUE = LIST_PAGE_SIZE
